'use client';

import { useCallback } from 'react';
import type { Reference } from '@apollo/client';
import { type ApolloCache, useApolloClient } from '@frameio/graphql-client';
import {
  FolderName,
  type FolderNameFragment,
} from '@frameio/graphql-client/generated';
import { useCreateUploadLinkMutation } from '@frameio/graphql-client/graphql/features/upload-links/mutations/generated/CreateUploadLink';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import type { UploadLinkCreatedSource } from '@frameio/shared/upload-links/constants';
import { useToast } from '@frameio/vapor';
import { beginDeferredClipboardWrite } from '@frameio/vapor-migration/hooks/useCopyText/deferredClipboardWrite';
import { useUploadLinkModal } from '../components/UploadLinkModalProvider';
import TEXT from './generated/TEXT/useCreateUploadLink';

TEXT.DEFINE({
  /** Error toast shown when creating an upload link fails */
  error: 'Something went wrong. Upload Link was not created.',
  /** Loading toast title shown while an upload link is being created */
  loading: 'Creating upload link…',
  /** Fallback default name when the target folder's name is unknown */
  defaultNameFallback: 'Upload Link',
});

const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/** Stable id so the loading toast can be updated/removed as the request resolves. */
const CREATE_TOAST_ID = 'create-upload-link';

/**
 * Reflects a newly created link in the project's cached upload-link count(s) so
 * the sidebar tally updates immediately, without waiting on a refetch. The
 * sidebar reads a count-only connection while the manage list reads one with
 * `nodes`; this bumps the count for both and prepends the new link wherever
 * `nodes` are tracked.
 */
export function addUploadLinkToProjectCount(
  cache: ApolloCache<any>,
  projectId: string,
  uploadLinkId: string
) {
  cache.modify({
    id: cache.identify({ __typename: 'Project', id: projectId }),
    fields: {
      uploadLinks(existing, { toReference, readField }) {
        if (!existing) return existing;

        // Count-only connections (e.g. the sidebar tally) have no nodes to dedupe.
        if (!existing.nodes) {
          return { ...existing, totalCount: (existing.totalCount ?? 0) + 1 };
        }

        const newLink = toReference({
          __typename: 'UploadLink',
          id: uploadLinkId,
        });
        if (!newLink) return existing;

        // Guard against a double insert (e.g. a concurrent fetch already
        // normalized the link into this list).
        const alreadyPresent = existing.nodes.some(
          (node: Reference) => readField('id', node) === uploadLinkId
        );
        if (alreadyPresent) return existing;

        return {
          ...existing,
          totalCount: (existing.totalCount ?? 0) + 1,
          nodes: [newLink, ...existing.nodes],
        };
      },
    },
  });
}

/**
 * Reflects a newly created link on its folder so both the Finder tag and the
 * folder's "Copy Upload Link" submenu update without a refetch: flips
 * `hasActiveUploadLinks` on and prepends the link to the cached folder
 * connection when it has already been loaded.
 */
export function addUploadLinkToFolder(
  cache: ApolloCache<any>,
  folderId: string,
  uploadLinkId: string
) {
  cache.modify({
    id: cache.identify({ __typename: 'FolderAsset', id: folderId }),
    fields: {
      hasActiveUploadLinks: () => true,
      uploadLinks(
        existing:
          | { nodes?: readonly Reference[]; totalCount?: number }
          | undefined,
        { toReference, readField }
      ) {
        // Only patch a submenu list that has already been loaded; otherwise it
        // is fetched fresh (and thus already current) when first opened.
        if (!existing?.nodes) return existing;

        const newLink = toReference({
          __typename: 'UploadLink',
          id: uploadLinkId,
        });
        if (!newLink) return existing;

        // Guard against a double insert (e.g. a concurrent fetch already
        // normalized the link into this list).
        const alreadyPresent = existing.nodes.some(
          (node) => readField('id', node) === uploadLinkId
        );
        if (alreadyPresent) return existing;

        return {
          ...existing,
          totalCount: (existing.totalCount ?? 0) + 1,
          nodes: [newLink, ...existing.nodes],
        };
      },
    },
  });
}

export function useCreateUploadLink() {
  const { open } = useUploadLinkModal();
  const { cache } = useApolloClient();
  const { addToast, updateToast, removeToast } = useToast();
  const [createUploadLinkMutation] = useCreateUploadLinkMutation();
  const track = useTrackUnsafe();

  const createUploadLink = useCallback(
    async (
      folderId: string,
      source: UploadLinkCreatedSource
    ): Promise<void> => {
      // Start the copy in the click so Safari allows it; finish it with the URL below.
      const clipboardWrite = beginDeferredClipboardWrite();

      const showErrorToast = () =>
        updateToast(
          CREATE_TOAST_ID,
          { title: TEXT.error, variant: 'error' },
          { duration: null }
        );

      addToast(
        { title: TEXT.loading, isLoading: true },
        { id: CREATE_TOAST_ID, duration: null }
      );

      const folder = cache.readFragment<FolderNameFragment>({
        id: cache.identify({ __typename: 'FolderAsset', id: folderId }),
        fragment: FolderName,
      });

      const expiresAt = new Date(Date.now() + ONE_WEEK_MS).toISOString();

      const name = folder?.name ? folder.name : TEXT.defaultNameFallback;

      let uploadLink;
      try {
        const { data } = await createUploadLinkMutation({
          variables: {
            input: { folderId, name, expiresAt },
          },
        });
        uploadLink = data?.createUploadLink?.uploadLink;
      } catch {
        clipboardWrite?.cancel();
        showErrorToast();
        return;
      }

      if (!uploadLink?.id || !uploadLink.url) {
        clipboardWrite?.cancel();
        showErrorToast();
        return;
      }

      addUploadLinkToFolder(cache, folderId, uploadLink.id);

      const projectId = uploadLink.project?.id;
      if (projectId) {
        addUploadLinkToProjectCount(cache, projectId, uploadLink.id);
      }

      // Finish the deferred copy now that we have the URL.
      await clipboardWrite?.complete(uploadLink.url);

      // The create flow auto-copies the URL above, so this is always true today;
      // kept as an explicit field so the event still reads correctly if a
      // non-copying create surface is added later.
      track('upload-link-created', {
        upload_link_id: uploadLink.id,
        project_id: projectId,
        is_upload_url_copied_automatically: true,
        source,
      });

      removeToast(CREATE_TOAST_ID);
      open(uploadLink.id, { folderId, autoCopiedUrl: uploadLink.url });
    },
    [
      open,
      cache,
      addToast,
      updateToast,
      removeToast,
      createUploadLinkMutation,
      track,
    ]
  );

  return { createUploadLink };
}
